import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import React, { Fragment, useState } from 'react'
import Link from 'next/link'
import { FaCaretDown, FaWhatsapp } from 'react-icons/fa'
import { MyLink } from '../MyLink'
import { NavDropdown, NavLinkItem, NavWhatsApp } from './NavDropdown'
import { whatsAppDisplay, whatsAppUrl } from 'utils/business-info'
import {
    ELECTRICIAN_HUB,
    HANDYMAN_HUB,
    HUB_META,
    PLUMBER_HUB,
    handymanJobPages,
    jobPagePath,
} from 'data/handyman-job-pages'

// Navbar extras for the handyman, electrician and plumber sections, modelled on
// AirconNavBar: service/problem dropdowns, a Pricing link, Blogs, and the
// WhatsApp number. The number used to sit inside the HandymanBooking card; it
// lives here now, as on the aircon pages, so it shows on every page of the section.

const job = (slug) => ({ href: jobPagePath(slug), label: handymanJobPages[slug].breadcrumbTitle })

const NAV = {
    handyman: {
        hubPath: HANDYMAN_HUB,
        menus: [
            {
                // Not "Services": IndexNav already has a site-wide Services menu.
                label: 'Handyman Services',
                items: [
                    job('tv-mounting'),
                    job('curtain-rail-installation'),
                    job('furniture-assembly'),
                    job('door-lock-replacement'),
                    { href: PLUMBER_HUB, label: 'Plumber Services' },
                    { href: ELECTRICIAN_HUB, label: 'Electrician Services' },
                ],
            },
            {
                label: 'Common Problems',
                items: [
                    job('leaking-faucet-sink-pipe-repair'),
                    job('clogged-toilet-bowl'),
                    job('clogged-kitchen-sink'),
                    job('clogged-bathroom-drain'),
                    job('power-trip-repair'),
                ],
            },
        ],
    },
    electrician: {
        hubPath: ELECTRICIAN_HUB,
        menus: [
            {
                label: 'Electrical Services',
                items: [
                    job('power-trip-repair'),
                    job('db-box-replacement'),
                    job('ceiling-fan-installation'),
                    job('water-heater-installation'),
                    { href: HANDYMAN_HUB, label: 'Handyman Services' },
                    { href: PLUMBER_HUB, label: 'Plumber Services' },
                ],
            },
        ],
    },
    plumber: {
        hubPath: PLUMBER_HUB,
        menus: [
            {
                label: 'Plumbing Problems',
                items: [
                    job('leaking-faucet-sink-pipe-repair'),
                    job('clogged-toilet-bowl'),
                    job('clogged-kitchen-sink'),
                    job('clogged-bathroom-drain'),
                    job('water-heater-installation'),
                    { href: HANDYMAN_HUB, label: 'Handyman Services' },
                    { href: ELECTRICIAN_HUB, label: 'Electrician Services' },
                ],
            },
        ],
    },
}

const BLOG_HREF = '/sg/blogs/repair-renovation'

const waHref = (hubPath) => `${whatsAppUrl}?text=${encodeURIComponent(HUB_META[hubPath].whatsAppText)}`

// Desktop: rendered inside IndexNav's <ul>, so every top-level node is an <li>.
export const HandymanNavBar = ({ section }) => {
    const nav = NAV[section]
    const meta = HUB_META[nav.hubPath]

    return (
        <>
            {nav.menus.map((menu) => (
                <NavDropdown key={menu.label} label={menu.label} items={menu.items} />
            ))}
            <NavLinkItem href={meta.pricePage} label="Pricing" ariaLabel={meta.priceLabel} />
            <NavLinkItem href={BLOG_HREF} label="Blogs" ariaLabel="Repair & Renovation Blogs" />
            <NavWhatsApp text={meta.whatsAppText} ariaLabel="WhatsApp StringsSG to book a service" />
        </>
    )
}

// Mobile: rendered inside IndexNav's hamburger menu, like AirconNavBarSmall.
export const HandymanNavBarSmall = ({ section }) => {
    const nav = NAV[section]
    const [ openMenu, setOpenMenu ] = useState(null)

    return (
        <>
            {nav.menus.map((menu, i) => {
                const id = `${section}-menu-${i}`
                const isOpen = openMenu === menu.label
                return (
                    <li key={menu.label}>
                        <div className="flex justify-center">
                            <button
                                type="button"
                                onClick={() => setOpenMenu(isOpen ? null : menu.label)}
                                className="flex justify-center items-center px-2 py-2 text-orange-600"
                                aria-expanded={isOpen}
                                aria-controls={id}
                                aria-haspopup="true"
                            >
                                {menu.label} <FaCaretDown className="h-5 w-5 ml-1" aria-hidden="true" />
                            </button>
                        </div>
                        {isOpen && (
                            <ul id={id} role="menu" className="relative">
                                {menu.items.map(({ href, label }) => (
                                    <MenuItem key={href} role="menuitem">
                                        <MyLink href={href} prefetch={false} className="text-orange-500 text-sm p-2 block">
                                            {label}
                                        </MyLink>
                                    </MenuItem>
                                ))}
                            </ul>
                        )}
                    </li>
                )
            })}
            <MenuItem>
                <MyLink href={HUB_META[nav.hubPath].pricePage} prefetch={false} className="flex justify-center px-2 py-2 text-orange-600" aria-label={HUB_META[nav.hubPath].priceLabel}>
                    Pricing
                </MyLink>
            </MenuItem>
            <MenuItem>
                <MyLink href={BLOG_HREF} prefetch={false} className="flex justify-center px-2 py-2 text-orange-600" aria-label="Repair & Renovation Blogs">
                    Blogs
                </MyLink>
            </MenuItem>
            <MenuItem as="li">
                {/* Primary contact CTA - server rendered so the number is crawlable */}
                <div className="flex justify-center pb-3">
                    <a
                        href={waHref(nav.hubPath)}
                        target="_blank"
                        rel="noreferrer"
                        aria-label="WhatsApp StringsSG to book a service"
                        className="flex items-center gap-2 bg-linear-to-tr from-green-700 to-green-500 text-white text-sm font-bold px-5 py-2.5 rounded-full shadow-lg"
                    >
                        <FaWhatsapp className="text-base" aria-hidden="true" />
                        WhatsApp {whatsAppDisplay}
                    </a>
                </div>
            </MenuItem>
        </>
    )
}
